import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { PageHint } from '@components/common/PageHint.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { toastService } from '@services/toast.service.ts';
import { getGoalOptions, getPmClientGoals, GoalOption, PmClientGoal } from '@services/pm-client-goals.service.ts';

const KIND_LABELS: Record<PmClientGoal['kind'], string> = {
  quantitative: 'Кількісна',
  qualitative: 'Якісна',
};

function goalDetails(goal: PmClientGoal): string {
  if (goal.kind === 'quantitative') {
    const unit = goal.unit ? ` ${goal.unit}` : '';
    const start = goal.startValue != null ? `${goal.startValue}${unit} → ` : '';
    return `${start}${goal.currentValue ?? '—'}${unit} → ${goal.targetValue ?? '—'}${unit}`;
  }
  return goal.desiredResult ?? '—';
}

// Цілі користувача (сутність UserGoal, модуль goals) для продакт-менеджера (2026-10-14, read-only),
// пласкою таблицею. НЕ плутати з табом "Денні норми" (PmClientDailyNormsTab) - той про старі денні норми
// (вода/калорії/БЖВ), зовсім інша сутність (docs/technical-debt.md, п.18 у api).
export const PmClientGoalsTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [goals, setGoals] = useState<PmClientGoal[]>([]);
  const [options, setOptions] = useState<GoalOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([getPmClientGoals(clientId), getGoalOptions()])
      .then(([goalsData, optionsData]) => {
        setGoals(goalsData);
        setOptions(optionsData);
      })
      .catch(() => toastService.error('Не вдалося завантажити цілі клієнта.'))
      .finally(() => setLoading(false));
  }, [clientId]);

  const labelFor = (typeCode: PmClientGoal['typeCode']) => options.find(o => o.typeCode === typeCode)?.label ?? typeCode;

  return (
    <Box data-i-doc={PageIndices.PM_CLIENT_GOALS}>
      <PageHint iDoc={PageIndices.PM_CLIENT_GOALS} />

      <PageCard>
        {loading ? (
          <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Box>
        ) : goals.length === 0 ? (
          <Typography sx={{ py: 4, textAlign: 'center' }} color="text.secondary">У клієнта ще нема цілей.</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Тип</TableCell>
                  <TableCell>Рід</TableCell>
                  <TableCell>Головна</TableCell>
                  <TableCell>Деталі</TableCell>
                  <TableCell>Дата</TableCell>
                  <TableCell>Оновлено</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {goals.map(goal => (
                  <TableRow key={goal.id} hover>
                    <TableCell>{labelFor(goal.typeCode)}</TableCell>
                    <TableCell>{KIND_LABELS[goal.kind]}</TableCell>
                    <TableCell>
                      {goal.isMain ? <Chip label="Головна" size="small" color="primary" /> : null}
                    </TableCell>
                    <TableCell>{goalDetails(goal)}</TableCell>
                    <TableCell>{goal.targetDate ?? '—'}</TableCell>
                    <TableCell>{new Date(goal.updatedAt).toLocaleDateString('uk-UA')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </PageCard>
    </Box>
  );
};
